import type { Spacing } from '@iheartjane/reefer';
import { Box, Flex, InfoIcon, Popover, Typography } from '@iheartjane/reefer';

import type { Terpene } from '@jane/bloom/types/schemas/marketplace/terpenes';
import {
  TERPENE_COLORS,
  TERPENE_DETAILS,
} from '@jane/bloom/utils/terpenes/constants';

interface TerpeneBarProps {
  actualPercentage: number;
  terpene: Terpene;
  terpeneNameMt?: Spacing;
  unit: string;
  variant?: 'row' | 'column';
  widthPercentage: number;
}

export const TerpeneBar = ({
  actualPercentage,
  terpene,
  terpeneNameMt,
  unit,
  variant = 'row',
  widthPercentage,
}: TerpeneBarProps) => {
  const details = TERPENE_DETAILS[terpene];
  const formattedTerpene = terpene.charAt(0).toUpperCase() + terpene.slice(1);
  const backgroundColor = TERPENE_COLORS[terpene];
  const formattedUnit =
    unit.toLowerCase() === 'mgpg' || unit.toLowerCase() === 'mg/g'
      ? ' mg/g'
      : '%';

  if (variant === 'column') {
    return (
      <div
        data-testid={`column-terpene-bar-container-${terpene}`}
        style={{ flexBasis: `${widthPercentage}%` }}
      >
        <Flex flexDirection="column">
          <Box
            borderRadius="lg"
            background={backgroundColor}
            height={8}
            width="100%"
          />
          <Typography mt={terpeneNameMt} truncateAt="100%">
            {formattedTerpene}
          </Typography>
        </Flex>
      </div>
    );
  }

  const popoverContent = details
    ? () => (
        <Popover.Content label={details}>
          <Typography>{details}</Typography>
        </Popover.Content>
      )
    : undefined;

  const terpeneBar = (
    <Flex width="100%" flexDirection="column">
      <Box
        background="grays-ultralight"
        width="100%"
        borderRadius="lg"
        height={8}
      >
        <Box
          borderRadius="lg"
          height={8}
          background={backgroundColor}
          data-testid={`row-terpene-bar-${terpene}`}
          width={`${widthPercentage}%`}
        />
      </Box>
      <Flex width="100%" justifyContent="space-between" flexWrap="wrap">
        <Typography mt={terpeneNameMt}>{formattedTerpene}</Typography>
        <Flex alignItems="center" gap={4}>
          <Typography>
            {actualPercentage}
            {formattedUnit}
          </Typography>
          {details && <InfoIcon size="sm" />}
        </Flex>
      </Flex>
    </Flex>
  );

  if (details) {
    return (
      <Popover
        closeOnTargetClick
        openOn="hover"
        label={details}
        target={terpeneBar}
        variant="info"
        targetWidth="100%"
      >
        {popoverContent}
      </Popover>
    );
  }

  return terpeneBar;
};
